import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, MapPin, Mic, Sparkles } from "lucide-react";
import styles from "./MainPage.module.scss";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";
import { DestinationCard } from "../components/DestinationCard";
import DESTINATIONS from "../data/destinations";
import { Destination } from "../types/destination";
import { useTheme } from "../context/ThemeContext";
import { useMediaQuery } from "../utilities/useMediaQuery";

/* The full example runs to 45 characters, which a phone's ask bar clips
   mid-word. The short one still shows the shape of a good prompt — a
   trip, a party size, a budget — in what actually fits. */
const ASK_PLACEHOLDER = "4-day beach trip with my wife, $1,000 budget.";
const ASK_PLACEHOLDER_SHORT = "Beach trip for two, $1,000";

/* ── Grid pattern: cycles 2 big / 4 medium / 3 medium per row ─── */
const GRID_PATTERNS: Array<"large" | "medium" | "small"> = ["large", "medium", "small"];

/* ── Filter options (from team4) ─────────────────── */
const REGIONS = ['All', 'USA', 'Europe', 'Asia', 'Latin America', 'Africa', 'Oceania'];
const BUDGETS = [
    { label: 'Any', min: 0, max: Infinity },
    { label: '$0–500', min: 0, max: 500 },
    { label: '$500–1K', min: 500, max: 1000 },
    { label: '$1K–2.5K', min: 1000, max: 2500 },
    { label: '$2.5K–5K', min: 2500, max: 5000 },
    { label: '$5K+', min: 5000, max: Infinity },
];
const ACTIVITIES = ['All', 'Beach', 'Nature', 'Culture', 'Wellness', 'Sport', 'Food', 'Adventure', 'Romance'];

/* ── Filter Dropdown ─────────────────────────────── */
function FilterDrop({ label, value, options, onChange }: {
    label: string;
    value: string | { label: string };
    options: (string | { label: string })[];
    onChange: (opt: any) => void;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    const displayValue = typeof value === 'object' ? value.label : value;

    return (
        <div className={styles.filter} ref={ref}>
            <button className={styles.filterBtn} onClick={() => setOpen(o => !o)} type="button">
                {label}: <strong>{displayValue}</strong>
                <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
            </button>
            {open && (
                <div className={styles.filterMenu}>
                    {options.map((opt, i) => {
                        const lbl = typeof opt === 'object' ? opt.label : opt;
                        const isActive = typeof opt === 'object' ? opt.label === displayValue : opt === displayValue;
                        return (
                            <button key={i} className={`${styles.filterOpt} ${isActive ? styles.filterOptActive : ''}`} onClick={() => { onChange(opt); setOpen(false); }}>
                                {lbl}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function buildRows(cards: Destination[]) {
    const weekendEscapes = cards.filter(c => c.price <= 900).slice(0, 8);
    const bestDeals = cards
        .filter(c => c.price >= 500 && c.price <= 1400)
        .sort((a, b) => (b.match || 0) - (a.match || 0))
        .slice(0, 8);
    const cultureCards = cards
        .filter(c => c.categories?.some(cat => ["culture", "food"].includes(cat)))
        .slice(0, 8);
    const adventureCards = cards
        .filter(c => c.categories?.some(cat => ["adventure"].includes(cat)))
        .slice(0, 8);
    const beachCards = cards
        .filter(c => c.categories?.some(cat => ["beach"].includes(cat)))
        .slice(0, 8);
    const trendingCards = [...cards]
        .sort((a, b) => (b.match || 0) - (a.match || 0))
        .slice(0, 8);

    return [
        { key: "weekend", title: "Weekend escapes", subtitle: "2–3 days · nearby · short flight", cards: weekendEscapes },
        { key: "deals", title: "Best deals", subtitle: "1–2 week trips · best price/quality combo", cards: bestDeals },
        { key: "trending", title: "Trending now", subtitle: "Most popular with travellers right now", cards: trendingCards },
        { key: "culture", title: "Culture & history", subtitle: "Museums · ancient ruins · theaters · heritage", cards: cultureCards },
        { key: "adventure", title: "Adventure & nature", subtitle: "Hiking · skiing · surfing · national parks", cards: adventureCards },
        { key: "beach", title: "Beach getaways", subtitle: "Sun · sand · crystal-clear water", cards: beachCards },
    ].filter(r => r.cards.length > 0);
}

export const MainPage = () => {
    const navigate = useNavigate();
    const { theme } = useTheme();
    const [input, setInput] = useState("");
    const isNarrow = useMediaQuery('(max-width: 640px)');
    const [savedSet, setSavedSet] = useState<Set<string>>(() => new Set());
    const [filterRegion, setFilterRegion] = useState("All");
    const [filterBudget, setFilterBudget] = useState(BUDGETS[0]);
    const [filterActivity, setFilterActivity] = useState("All");

    const allRows = useMemo(() => buildRows(DESTINATIONS as Destination[]), []);

    /* Apply filters to all rows */
    const rows = useMemo(() => {
        const hasFilters = filterRegion !== 'All' || filterBudget.label !== 'Any' || filterActivity !== 'All';
        if (!hasFilters) return allRows;

        return allRows.map(row => ({
            ...row,
            cards: row.cards.filter(c => {
                if (filterRegion !== 'All' && c.region && !c.region.toLowerCase().includes(filterRegion.toLowerCase())) return false;
                if (filterBudget.min !== undefined && (c.price < filterBudget.min || c.price > filterBudget.max)) return false;
                if (filterActivity !== 'All' && (!c.categories || !c.categories.some(cat => cat.toLowerCase() === filterActivity.toLowerCase()))) return false;
                return true;
            }),
        })).filter(r => r.cards.length > 0);
    }, [allRows, filterRegion, filterBudget, filterActivity]);

    const hasActiveFilters = filterRegion !== 'All' || filterBudget.label !== 'Any' || filterActivity !== 'All';

    const toggleSave = (slug: string) => {
        setSavedSet(prev => {
            const n = new Set(prev);
            if (n.has(slug)) n.delete(slug);
            else n.add(slug);
            return n;
        });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (input.trim()) navigate("/chat", { state: { message: input } });
    };

    return (
        <div className={styles.mainPage}>
            <Header />

            {/* ─── HERO ───
                Old glow-on-flat-background treatment, replaced by the
                full-bleed photo below. Kept in case the photo hero doesn't
                stick.
                <div className={styles.heroGlow} />
            */}
            {/* Cinematic photo hero. The photo, the three scrims and the content
                are separate layers so the photo can drift without carrying the
                type with it, and so the light theme can swap the photograph and
                flip the scrims from black to white underneath it. */}
            <section className={styles.hero}>
                <div className={styles.heroPhoto} />
                <div className={styles.scrimA} />
                <div className={styles.scrimB} />
                <div className={styles.scrimC} />
                <div className={styles.heroInner}>
                    {/* <div className={styles.eyebrow}>
                        <span className={styles.eyebrowLine} />
                        AI travel assistant
                    </div> */}

                    <h1 className={styles.headline}>
                        Say where you<br />
                        want to be.
                    </h1>

                    <p className={styles.heroSubtitle}>
                        Adiona plans, prices and books the whole trip — flights, the room, and everything in between.
                    </p>

                    {/* Search bar */}
                    <form onSubmit={handleSubmit} className={styles.searchForm}>
                        <div className={styles.searchBar}>
                            <Sparkles size={17} className={styles.searchIcon} aria-hidden="true" />
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder={isNarrow ? ASK_PLACEHOLDER_SHORT : ASK_PLACEHOLDER}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                            />
                            {/* Decorative — there's no voice input behind it yet, matches the reference layout */}
                            <Mic size={17} className={styles.micIcon} aria-hidden="true" />
                            <button type="submit" className={styles.sendBtn} disabled={!input.trim()}>
                                <ArrowRight size={18} strokeWidth={2.5} />
                            </button>
                        </div>
                    </form>

                    {/* Prompt chips */}
                    <div className={styles.prompts}>
                        <button
                            className={styles.promptPill}
                            type="button"
                            onClick={() => navigate("/chat", { state: { message: "Business trip to Chicago next week, 2 nights, economy class, budget hotel near downtown" } })}
                        >
                            Business trip to Chicago next week
                        </button>
                        <button
                            className={styles.promptPill}
                            type="button"
                            onClick={() => navigate("/chat", { state: { message: "Beach vacation for a family of 4 (2 adults, 2 kids aged 6 and 9), 7 days, all-inclusive resort, budget $3,500" } })}
                        >
                            Relax on the sea with my family
                        </button>
                        <button
                            className={styles.promptPill}
                            type="button"
                            onClick={() => navigate("/chat", { state: { message: "Romantic weekend getaway for 2, under $1,200, 3 nights, boutique hotel, good food and wine" } })}
                        >
                            Romantic weekend getaway for two
                        </button>
                        <button
                            className={styles.promptPill}
                            type="button"
                            onClick={() => navigate("/chat", { state: { message: "Surprise me with a weekend getaway somewhere I haven't been, budget around $900" } })}
                        >
                            Surprise me this weekend
                        </button>
                    </div>
                </div>

                {/* The caption names the photograph, and the photograph now
                    changes with the theme — so it can't stay hardcoded to the
                    dark one. The light photo's location is unconfirmed, so it
                    shows no caption rather than the wrong one. */}
                {theme !== 'light' && (
                    <div className={styles.heroCaption}>
                        <MapPin size={13} aria-hidden="true" /> Lago di Braies, Dolomites
                    </div>
                )}
            </section>

            {/* ─── DESTINATION ROWS ─── */}
            <section className={styles.recs}>
                {/* Filter bar */}
                <div className={styles.filterBar}>
                    <div className={styles.filters}>
                        <FilterDrop label="Region" value={filterRegion} options={REGIONS} onChange={setFilterRegion} />
                        <FilterDrop label="Budget" value={filterBudget} options={BUDGETS} onChange={setFilterBudget} />
                        <FilterDrop label="Activity" value={filterActivity} options={ACTIVITIES} onChange={setFilterActivity} />
                    </div>
                    {hasActiveFilters && (
                        <button className={styles.clearFilters} type="button" onClick={() => { setFilterRegion('All'); setFilterBudget(BUDGETS[0]); setFilterActivity('All'); }}>
                            Clear filters
                        </button>
                    )}
                </div>

                {/* Active chips */}
                {hasActiveFilters && (
                    <div className={styles.chips}>
                        {filterRegion !== 'All' && (
                            <span className={styles.chip}>{filterRegion}<button onClick={() => setFilterRegion('All')} type="button">&times;</button></span>
                        )}
                        {filterBudget.label !== 'Any' && (
                            <span className={styles.chip}>{filterBudget.label}<button onClick={() => setFilterBudget(BUDGETS[0])} type="button">&times;</button></span>
                        )}
                        {filterActivity !== 'All' && (
                            <span className={styles.chip}>{filterActivity}<button onClick={() => setFilterActivity('All')} type="button">&times;</button></span>
                        )}
                    </div>
                )}

                {rows.map((row, idx) => {
                    const size = GRID_PATTERNS[idx % 3];
                    const colCount = size === "large" ? 2 : size === "medium" ? 3 : 4;
                    const visibleCards = row.cards.slice(0, colCount);
                    return (
                        <div key={row.key} className={styles.row}>
                            <div className={styles.rowHeader}>
                                <div>
                                    <h3 className={styles.rowTitle}>{row.title}</h3>
                                    <p className={styles.rowSubtitle}>{row.subtitle}</p>
                                </div>
                            </div>
                            {/* The count goes in as a custom property rather than as
                                grid-template-columns itself, so the responsive steps in
                                the stylesheet can override it without !important. */}
                            <div
                                className={styles.grid}
                                style={{ "--cols": colCount } as React.CSSProperties}
                            >
                                {visibleCards.map((card) => (
                                    <DestinationCard
                                        key={card.slug}
                                        destination={card}
                                        size={size}
                                        saved={savedSet.has(card.slug)}
                                        onToggleSave={toggleSave}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}

                {rows.length === 0 && hasActiveFilters && (
                    <div className={styles.emptyState}>
                        <p>No destinations match your filters.</p>
                        <button className={styles.clearFilters} type="button" onClick={() => { setFilterRegion('All'); setFilterBudget(BUDGETS[0]); setFilterActivity('All'); }}>
                            Clear all filters
                        </button>
                    </div>
                )}
            </section>

            <Footer />
        </div>
    );
};
